import React, { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Col, Form, Row } from 'react-bootstrap';
import { FaRegCalendar } from 'react-icons/fa6';
import { Formik } from 'formik';
import orderBy from 'lodash/orderBy';

import FORMATTERS from 'helpers/formatters';
import { Skeleton } from '_components/_core';

import LineChart from './LineChart';
import List from './List';
import {
  CustomCard,
  CustomCardBody,
  Label,
  StyledFormSelectField,
  Value,
} from './styles';

function BillingOverview({ billingOverview, onFetchBillingOverview, isLoading }) {
  useEffect(() => {
    const oldValues = JSON.parse(localStorage.getItem('adminBillingOverview'));

    if (oldValues) {
      onFetchBillingOverview({
        ...oldValues,
        provider: oldValues.provider === 'ALL' ? null : oldValues.provider,
      });
    } else {
      onFetchBillingOverview({
        start_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
        end_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
        quick_date: 'thisMonth',
        date_type: 'payment_date',
        provider: null,
      });
    }
  }, [onFetchBillingOverview]);

  const initialValues = useMemo(() => {
    const oldValues = JSON.parse(localStorage.getItem('adminBillingOverview'));

    if (oldValues) {
      return oldValues;
    }

    return {
      start_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
      end_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
      quick_date: 'thisMonth',
      date_type: 'payment_date',
      provider: 'ALL',
    };
  }, []);

  const storeInLocalStorage = useCallback(values => {
    localStorage.setItem('adminBillingOverview', JSON.stringify(values));
  }, []);

  const handleRefreshOverview = useCallback(
    values => {
      storeInLocalStorage(values);

      onFetchBillingOverview({
        ...values,
        provider: values.provider === 'ALL' ? null : values.provider,
      });
    },
    [onFetchBillingOverview, storeInLocalStorage],
  );

  const handleQuickDateChange = useCallback(
    (quickDate, _values, setFieldValue, submitForm) => {
      const now = DateTime.now();

      const format = date => date.toFormat('yyyy-MM-dd HH:mm:ss');

      switch (quickDate) {
        case 'today':
          setFieldValue('start_date', format(now.startOf('day')));
          setFieldValue('end_date', format(now.endOf('day')));
          break;
        case 'thisMonth':
          setFieldValue('start_date', format(now.startOf('month')));
          setFieldValue('end_date', format(now.endOf('month')));
          break;
        case 'thisYear':
          setFieldValue('start_date', format(now.startOf('year')));
          setFieldValue('end_date', format(now.endOf('year')));
          break;
        case 'allTime':
          setFieldValue(
            'start_date',
            format(DateTime.fromObject({ year: 2023 }).startOf('year')),
          );
          setFieldValue('end_date', format(now.endOf('year')));
          break;
        case 'custom':
          setFieldValue('start_date', format(now.startOf('month')));
          setFieldValue('end_date', format(now.endOf('month')));
          break;
        default:
          break;
      }

      setFieldValue('quick_date', quickDate);

      if (quickDate !== 'custom') {
        submitForm();
      }
    },
    [],
  );

  const renderCard = useCallback(
    ({
      title,
      value,
      chart,
      list,
      formatNumber = true,
      formatPercent = false,
      className,
    }) => {
      let formattedValue = value;

      if (formatNumber) {
        formattedValue = FORMATTERS.NUMBER(value);
      }

      if (formatPercent) {
        formattedValue = FORMATTERS.CHART_PERCENTAGE(value);
      }

      return (
        <CustomCard className={className}>
          <CustomCardBody>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex flex-column align-items-start">
                <Label>{title}</Label>
                {!isLoading && <Value variant="large">{formattedValue}</Value>}
                {isLoading && <Skeleton className={'mt-2 mb-2'} />}
              </div>
              {/* <FaMoneyBillWave size={24} className="text-muted" /> */}
            </div>
            {chart}
            {list}
          </CustomCardBody>
        </CustomCard>
      );
    },
    [isLoading],
  );

  const invoicesByStatus = useMemo(() => {
    if (!billingOverview.payments) {
      return [];
    }

    const list = [
      {
        status: 'Pagas',
        quantity: billingOverview.payments?.paid.count || 0,
        amount: billingOverview.payments?.paid.total || 0,
        variant: 'success',
      },
      {
        status: 'Rascunho',
        quantity: billingOverview.payments?.draft.count || 0,
        amount: billingOverview.payments?.draft.total || 0,
        variant: 'info',
      },
      {
        status: 'Anuladas',
        quantity: billingOverview.payments?.voided.count || 0,
        amount: billingOverview.payments?.voided.total || 0,
        variant: 'danger',
      },
      {
        status: 'Pendentes',
        quantity: billingOverview.payments?.open.count || 0,
        amount: billingOverview.payments?.open.total || 0,
        variant: 'warning',
      },
      {
        status: 'Vencidas',
        quantity: billingOverview.payments?.overdue.count || 0,
        amount: billingOverview.payments?.overdue.total || 0,
        variant: 'danger',
      },
    ];

    return orderBy(list, ['quantity', 'amount'], ['desc', 'desc']);
  }, [billingOverview]);

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={handleRefreshOverview}>
        {({ values, handleSubmit, setFieldValue, submitForm }) => (
          <>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col sm={12} className="d-flex align-items-center justify-content-start">
                  <StyledFormSelectField
                    name="quick_date"
                    placeholder="Período"
                    options={[
                      {
                        value: 'today',
                        label: 'Hoje',
                        icon: <FaRegCalendar />,
                      },
                      {
                        value: 'thisMonth',
                        label: 'Este Mês',
                        icon: <FaRegCalendar />,
                      },
                      {
                        value: 'thisYear',
                        label: 'Este Ano',
                        icon: <FaRegCalendar />,
                      },
                      {
                        value: 'allTime',
                        label: 'Desde o início',
                        icon: <FaRegCalendar />,
                      },
                      {
                        value: 'custom',
                        label: 'Personalizado',
                        icon: <FaRegCalendar />,
                      },
                    ]}
                    onChange={option =>
                      handleQuickDateChange(
                        option.value,
                        values,
                        setFieldValue,
                        submitForm,
                      )
                    }
                    isSearchable={false}
                  />
                  <StyledFormSelectField
                    name="date_type"
                    label="Tipo de Data"
                    options={[
                      { value: 'due_date', label: 'Data de Vencimento' },
                      { value: 'payment_date', label: 'Data de Pagamento' },
                      { value: 'issue_date', label: 'Data de Emissão' },
                    ]}
                    width="200px"
                    className="ml-2"
                    onChange={option => {
                      setFieldValue('date_type', option.value);

                      submitForm();
                    }}
                  />
                  <StyledFormSelectField
                    name="provider"
                    label="Provedor"
                    options={[
                      { value: 'STRIPE', label: 'STRIPE' },
                      { value: 'ASAAS', label: 'ASAAS' },
                      { value: 'ALL', label: 'Todos' },
                    ]}
                    width="200px"
                    className="ml-2"
                    onChange={option => {
                      setFieldValue('provider', option.value);

                      submitForm();
                    }}
                  />
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Formik>
      <Row className="mt-3">
        <Col lg={4}>
          <Row>
            <Col>
              {renderCard({
                title: 'Volume bruto hoje',
                value: billingOverview.today_total,
              })}
            </Col>
            <Col>
              {renderCard({
                title: 'Volume bruto ontem',
                value: billingOverview.yesterday_total,
              })}
            </Col>
          </Row>
          <Row>
            <Col>
              {renderCard({
                title: 'Volume bruto',
                value:
                  billingOverview.gross_volume?.data?.reduce(
                    (acc, value) => acc + value,
                    0,
                  ) || 0,
                chart: (
                  <LineChart
                    data={{
                      categories: billingOverview.gross_volume?.categories,
                      series: [
                        {
                          animation: true,
                          name: 'Volume bruto',
                          color: '#9966FF',
                          data: billingOverview.gross_volume?.data,
                        },
                      ],
                    }}
                    formatNumber
                  />
                ),
                className: 'mt-3',
              })}
            </Col>
          </Row>
        </Col>
        <Col lg={4}>
          <Row>
            <Col>
              {renderCard({
                title: 'MRR',
                value: billingOverview.mrr?.total || 0,
              })}
            </Col>
            <Col>
              {renderCard({
                title: 'Churn',
                value: billingOverview.churn_rate?.value || 0,
                formatNumber: false,
                formatPercent: true,
              })}
            </Col>
          </Row>
          <Row>
            <Col>
              {renderCard({
                title: 'Pagamentos concluídos',
                value:
                  billingOverview.concluded_payments?.data?.reduce(
                    (acc, value) => acc + value,
                    0,
                  ) || 0,
                formatNumber: false,
                chart: (
                  <LineChart
                    data={{
                      categories: billingOverview.concluded_payments?.categories,
                      series: [
                        {
                          animation: true,
                          name: 'Pagamentos concluídos',
                          color: '#9966FF',
                          data: billingOverview.concluded_payments?.data,
                        },
                      ],
                    }}
                    formatNumber={false}
                  />
                ),
                className: 'mt-3',
              })}
            </Col>
          </Row>
        </Col>
        <Col lg={4}>
          {renderCard({
            title: 'Faturas por status',
            value: billingOverview.payments?.total_invoices || 0,
            formatNumber: false,
            list: <List data={invoicesByStatus} formatNumber={false} />,
          })}
        </Col>
      </Row>
    </>
  );
}

export default BillingOverview;
